export * from "./constants";
export * from "./runtime";

import {
  getArcaneTokenMintForNetwork,
  getDepositLookupTableForNetwork,
  getDepositQueueForNetwork,
  getIndexerUrlForNetwork,
  getRiskApiUrlForNetwork,
  getTokenDecimals,
  getTokenMintsForNetwork,
  type TokenDecimalsSymbol,
  type TokenSymbol,
} from "./constants";
import { getConfig } from "./runtime";

export type AllowedToken = {
  symbol: TokenDecimalsSymbol;
  mint: string;
  decimals: number;
};

/** Resolve a mint address to a supported token on the active cluster. */
export function resolveAllowedTokenFromMint(
  mint: string
): AllowedToken {
  const normalized = mint.trim();
  const tokenMints = getTokenMintsForNetwork(getConfig().network);

  for (const [symbol, configuredMint] of Object.entries(tokenMints) as [
    TokenDecimalsSymbol,
    string,
  ][]) {
    if (configuredMint && configuredMint === normalized) {
      return {
        symbol,
        mint: normalized,
        decimals: getTokenDecimals(symbol),
      };
    }
  }

  throw new Error(
    `arcane-sdk: token mint ${normalized} is not supported on ${getConfig().network}`
  );
}

/** Range API key supplied via {@link init}. */
export function getRangeApiKey(): string {
  return getConfig().rangeApiKey;
}

/** Solana RPC endpoint supplied via {@link init}. */
export function getRpcEndpoint(): string {
  return getConfig().rpcEndpoint;
}

/** Risk API URL for the active cluster. */
export function getRiskApiUrl(): string {
  return getRiskApiUrlForNetwork(getConfig().network);
}

/** Active cluster (inferred from RPC endpoint or init override). */
export function getNetwork() {
  return getConfig().network;
}

/** Program ID for the active cluster. */
export function getProgramIdString(): string {
  return getConfig().programId;
}

/** Indexer base URL for the active cluster. */
export function getIndexerUrl(): string {
  return getIndexerUrlForNetwork(getConfig().network);
}

/** Token mints for the active cluster. */
export function getTokenMints() {
  return getTokenMintsForNetwork(getConfig().network);
}

/** Arcane token mint for the active cluster (also the relayer registry mint). */
export function getArcaneTokenMint(): string {
  return getArcaneTokenMintForNetwork(getConfig().network);
}

export function getTokenMint(symbol: TokenSymbol): string {
  return getTokenMintsForNetwork(getConfig().network)[symbol];
}

/** Switchboard queue account for deposit oracle verification on the active cluster. */
export function getDepositQueueAddress(): string {
  return getDepositQueueForNetwork(getConfig().network);
}

/** Address lookup table for batched deposit transactions on the active cluster. */
export function getDepositLookupTableAddress(): string {
  return getDepositLookupTableForNetwork(getConfig().network);
}
